// src/components/core/FilterBar.jsx
import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Badge,
  useTheme,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  alpha,
  Popover,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  DatePicker,
  Autocomplete,
  ButtonGroup,
  Switch,
  FormControlLabel,
  Paper,
  Fade,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as TuneIcon,
  ViewList as ViewListIcon,
  Save as SaveIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  SwapVert as SwapVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';

/**
 * Unified filter bar component with search, filter, and sort functionality
 * 
 * @param {Object} props - Component props
 * @param {string} props.searchTerm - Current search term
 * @param {Function} props.onSearchChange - Function called when search term changes
 * @param {Function} props.onClearSearch - Function called when search is cleared
 * @param {Function} props.onOpenFilterMenu - Function to open filter menu with custom implementation
 * @param {Function} props.onOpenSortMenu - Function to open sort menu with custom implementation
 * @param {Function} props.onAdd - Function called when add button is clicked
 * @param {boolean} props.hasActiveFilters - Whether any filters are active
 * @param {boolean} props.canEdit - Whether user can add items
 * @param {string} props.addButtonLabel - Label for the add button
 * @param {string} props.searchPlaceholder - Placeholder text for search field
 * @param {boolean} props.hideSearch - Whether to hide the search field
 * @param {object} props.additionalButtons - Additional buttons to render
 * @param {object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Function to handle filter changes
 * @param {Array} props.filterConfig - Configuration for filter fields
 * @param {Function} props.onClearAllFilters - Function to clear all filters
 * @param {object} props.sort - Sort configuration { field, direction }
 * @param {Function} props.onSortChange - Function to handle sort field changes
 * @param {Array} props.sortOptions - Available sort options
 * @param {Array} props.activeFilters - Array of active filter objects for chips
 * @param {boolean} props.showActiveFilters - Whether to show active filter chips
 * @param {boolean} props.built-inMenus - Use built-in menus instead of external ones
 * @param {Array} props.quickFilters - Quick filter buttons configuration
 * @param {boolean} props.enableSavedFilters - Whether to show saved filters functionality
 * @param {Array} props.savedFilters - Saved filter presets
 * @param {Function} props.onSaveFilter - Function to save current filter state
 * @param {boolean} props.enableAdvancedFilters - Whether to show advanced filter panel
 * @param {Object} props.advancedFilters - Advanced filter configuration
 * @param {Function} props.onAdvancedFilterChange - Function to handle advanced filter changes
 */
const FilterBar = ({
  searchTerm = '',
  onSearchChange,
  onClearSearch,
  onOpenFilterMenu,
  onOpenSortMenu,
  onAdd,
  hasActiveFilters = false,
  canEdit = false,
  addButtonLabel = 'Add Item',
  searchPlaceholder = 'Search...',
  hideSearch = false, // New prop to hide search
  additionalButtons = null,
  filters = {},
  onFilterChange,
  filterConfig = [],
  onClearAllFilters,
  sort = { field: 'name', direction: 'asc' },
  onSortChange,
  sortOptions = [],
  activeFilters = [],
  showActiveFilters = false,
  builtInMenus = false,
  quickFilters = [],
  enableSavedFilters = false,
  savedFilters = [],
  onSaveFilter,
  enableAdvancedFilters = false,
  advancedFilters = {},
  onAdvancedFilterChange
}) => {
  const theme = useTheme();
  
  // Local state for built-in menus
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [savedFiltersAnchorEl, setSavedFiltersAnchorEl] = useState(null);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const [showSaveFilterDialog, setShowSaveFilterDialog] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  
  // Handle search input change
  const handleSearchChange = (e) => {
    if (onSearchChange) {
      onSearchChange(e.target.value);
    }
  };
  
  // Handle search clear
  const handleClearSearch = () => {
    if (onClearSearch) {
      onClearSearch();
    }
  };
  
  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
  };

  // Handle filter menu with local or external implementation
  const handleFilterOpen = (e) => {
    if (builtInMenus) {
      setFilterAnchorEl(e.currentTarget);
    } else if (onOpenFilterMenu) {
      onOpenFilterMenu(e);
    }
  };

  const handleFilterClose = () => {
    if (builtInMenus) {
      setFilterAnchorEl(null);
    }
  };

  // Handle sort menu with local or external implementation
  const handleSortOpen = (e) => {
    if (builtInMenus) {
      setSortAnchorEl(e.currentTarget);
    } else if (onOpenSortMenu) {
      onOpenSortMenu(e);
    }
  };

  const handleSortClose = () => {
    if (builtInMenus) {
      setSortAnchorEl(null);
    }
  };

  // Handle built-in sort option select
  const handleSortSelect = (field) => {
    if (onSortChange) {
      const direction = sort.field === field 
        ? (sort.direction === 'asc' ? 'desc' : 'asc')
        : 'asc';
      onSortChange(field, direction);
    }
    handleSortClose();
  };

  // Handle built-in filter change
  const handleFilterSelect = (name, value) => {
    if (onFilterChange) {
      onFilterChange(name, value);
    }
  };

  // Handle quick filter selection
  const handleQuickFilter = (filter) => {
    setActiveQuickFilter(filter.key);
    if (filter.onClick) {
      filter.onClick(filter);
    }
  };

  // Handle saved filters menu
  const handleSavedFiltersOpen = (e) => {
    setSavedFiltersAnchorEl(e.currentTarget);
  };

  const handleSavedFiltersClose = () => {
    setSavedFiltersAnchorEl(null);
  };

  const handleApplySavedFilter = (savedFilter) => {
    if (savedFilter.onApply) {
      savedFilter.onApply(savedFilter);
    }
    handleSavedFiltersClose();
  };

  // Handle advanced filters
  const handleAdvancedFiltersToggle = () => {
    setAdvancedFiltersOpen(!advancedFiltersOpen);
  };

  const handleAdvancedFilterChange = (filterKey, value) => {
    if (onAdvancedFilterChange) {
      onAdvancedFilterChange(filterKey, value);
    }
  };

  // Handle save filter
  const handleSaveFilter = () => {
    if (onSaveFilter && newFilterName.trim()) {
      onSaveFilter(newFilterName.trim());
      setNewFilterName('');
      setShowSaveFilterDialog(false);
    }
  };

  // Build-in filter menu
  const renderFilterMenu = () => {
    if (!builtInMenus) return null;

    return (
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: { 
            width: 280,
            p: 2,
            // borderRadius: theme.shape.borderRadius * 2,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[8],
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ 
          mb: 2,
          background: `linear-gradient(90deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          color: 'transparent'
        }}>
          Filter Your Search
        </Typography>
        
        <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.text.primary, 0.1) }} />
        
        <Box sx={{ pb: 1 }}>
          {filterConfig.map((filter, index) => (
            <FormControl key={filter.name} fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: theme.palette.text.secondary }}>
                {filter.label}
              </InputLabel>
              <Select
                name={filter.name}
                value={filters[filter.name] || 'all'}
                onChange={(e) => handleFilterSelect(filter.name, e.target.value)}
                label={filter.label}
                size="small"
                sx={{
                  // borderRadius: theme.shape.borderRadius * 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.text.primary, 0.1),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                    boxShadow: `0 0 0 3px ${theme.palette.secondary.main}40`,
                  },
                }}
              >
                <MenuItem value="all">{filter.allLabel || `All ${filter.label}`}</MenuItem>
                {(filter.options || []).map(option => (
                  <MenuItem 
                    key={option.value} 
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={onClearAllFilters}
              sx={{ 
                borderRadius: theme.aghrba?.tokens?.borderRadius?.button || 30,
                borderColor: alpha(theme.palette.text.primary, 0.2),
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                height: theme.aghrba?.tokens?.buttonHeight?.small || 36,
                '&:hover': {
                  transform: 'translateY(-2px)',
                }
              }}
            >
              Clear All
            </Button>
            <Button 
              variant="contained" 
              size="small"
              color="secondary"
              onClick={handleFilterClose}
              sx={{ 
                borderRadius: theme.aghrba?.tokens?.borderRadius?.button || 30,
                boxShadow: '0 4px 10px rgba(242, 197, 38, 0.3)',
                background: theme.aghrba?.gradients?.secondaryButton || `linear-gradient(45deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
                animation: 'glow 3s infinite',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                position: 'relative',
                overflow: 'hidden',
                height: theme.aghrba?.tokens?.buttonHeight?.small || 36,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 12px ${alpha(theme.palette.secondary.main, 0.4)}`,
                  background: `linear-gradient(45deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transition: 'left 0.7s',
                },
                '&:hover::before': {
                  left: '100%',
                },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Menu>
    );
  };

  // Built-in sort menu
  const renderSortMenu = () => {
    if (!builtInMenus) return null;

    return (
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
        PaperProps={{
          sx: { 
            width: 200,
            p: 1.5,
            // borderRadius: theme.shape.borderRadius * 2,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[8],
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ 
          px: 1, 
          mb: 1,
          background: `linear-gradient(90deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          color: 'transparent'
        }}>
          Sort By
        </Typography>
        
        <Divider sx={{ mb: 1.5 }} />
        
        {sortOptions.map(option => (
          <MenuItem
            key={option.field}
            onClick={() => handleSortSelect(option.field)}
            selected={sort.field === option.field}
            sx={{
              // borderRadius: theme.shape.borderRadius,
              mb: 0.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                color: theme.palette.secondary.main,
              },
            }}
          >
            {option.label} {sort.field === option.field && (sort.direction === 'asc' ? '↑' : '↓')}
          </MenuItem>
        ))}
      </Menu>
    );
  };

  // Render quick filters
  const renderQuickFilters = () => {
    if (!quickFilters.length) return null;

    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {quickFilters.map((filter, index) => (
          <Button
            key={filter.key || index}
            variant={activeQuickFilter === filter.key ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleQuickFilter(filter)}
            startIcon={filter.icon}
            sx={{
              borderRadius: 20,
              height: 32,
              fontSize: '0.75rem',
              textTransform: 'none',
              minWidth: 'auto',
              px: 2,
              borderColor: alpha(theme.palette.text.primary, 0.2),
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              ...(activeQuickFilter === filter.key && {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }),
            }}
          >
            {filter.label}
            {filter.count !== undefined && (
              <Chip 
                label={filter.count} 
                size="small" 
                sx={{ 
                  ml: 1, 
                  height: 18, 
                  fontSize: '0.7rem',
                  backgroundColor: activeQuickFilter === filter.key 
                    ? alpha(theme.palette.primary.contrastText, 0.2)
                    : alpha(theme.palette.primary.main, 0.1),
                  color: activeQuickFilter === filter.key 
                    ? theme.palette.primary.contrastText
                    : theme.palette.primary.main,
                }} 
              />
            )}
          </Button>
        ))}
      </Box>
    );
  };

  // Render saved filters menu
  const renderSavedFiltersMenu = () => {
    if (!enableSavedFilters) return null;

    return (
      <Menu
        anchorEl={savedFiltersAnchorEl}
        open={Boolean(savedFiltersAnchorEl)}
        onClose={handleSavedFiltersClose}
        PaperProps={{
          sx: {
            minWidth: 250,
            maxHeight: 400,
            borderRadius: 2,
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Saved Filters
        </Typography>
        
        <Divider />
        
        {savedFilters.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No saved filters
            </Typography>
          </MenuItem>
        ) : (
          savedFilters.map((savedFilter, index) => (
            <MenuItem
              key={savedFilter.id || index}
              onClick={() => handleApplySavedFilter(savedFilter)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 1.5,
              }}
            >
              <Box>
                <Typography variant="body2">{savedFilter.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {savedFilter.description || `${Object.keys(savedFilter.filters || {}).length} filters`}
                </Typography>
              </Box>
              <BookmarkIcon fontSize="small" color="primary" />
            </MenuItem>
          ))
        )}
        
        <Divider />
        
        <MenuItem onClick={() => setShowSaveFilterDialog(true)}>
          <SaveIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2">Save Current Filter</Typography>
        </MenuItem>
      </Menu>
    );
  };

  // Render advanced filters panel
  const renderAdvancedFilters = () => {
    if (!enableAdvancedFilters || !advancedFilters.fields) return null;

    return (
      <Collapse in={advancedFiltersOpen}>
        <Paper
          sx={{
            mt: 2,
            p: 3,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600 }}>
            Advanced Filters
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
            {advancedFilters.fields.map((field, index) => (
              <Box key={field.name || index}>
                {field.type === 'select' && (
                  <FormControl fullWidth size="small">
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      value={advancedFilters.values?.[field.name] || ''}
                      onChange={(e) => handleAdvancedFilterChange(field.name, e.target.value)}
                      label={field.label}
                    >
                      {field.options?.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                
                {field.type === 'autocomplete' && (
                  <Autocomplete
                    options={field.options || []}
                    value={advancedFilters.values?.[field.name] || null}
                    onChange={(e, value) => handleAdvancedFilterChange(field.name, value)}
                    renderInput={(params) => (
                      <TextField {...params} label={field.label} size="small" />
                    )}
                    multiple={field.multiple}
                    size="small"
                  />
                )}
                
                {field.type === 'date-range' && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      {field.label}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        type="date"
                        label="From"
                        size="small"
                        value={advancedFilters.values?.[`${field.name}_from`] || ''}
                        onChange={(e) => handleAdvancedFilterChange(`${field.name}_from`, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        type="date"
                        label="To"
                        size="small"
                        value={advancedFilters.values?.[`${field.name}_to`] || ''}
                        onChange={(e) => handleAdvancedFilterChange(`${field.name}_to`, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  </Box>
                )}
                
                {field.type === 'range' && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      {field.label}: {advancedFilters.values?.[field.name] || field.defaultValue || [field.min, field.max]}
                    </Typography>
                    <Slider
                      value={advancedFilters.values?.[field.name] || field.defaultValue || [field.min, field.max]}
                      onChange={(e, value) => handleAdvancedFilterChange(field.name, value)}
                      min={field.min}
                      max={field.max}
                      step={field.step || 1}
                      marks={field.marks}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                )}
                
                {field.type === 'switch' && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={advancedFilters.values?.[field.name] || false}
                        onChange={(e) => handleAdvancedFilterChange(field.name, e.target.checked)}
                      />
                    }
                    label={field.label}
                  />
                )}
              </Box>
            ))}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => {
                if (advancedFilters.onClear) {
                  advancedFilters.onClear();
                }
              }}
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              onClick={() => setAdvancedFiltersOpen(false)}
            >
              Apply Filters
            </Button>
          </Box>
        </Paper>
      </Collapse>
    );
  };

  // Render save filter dialog
  const renderSaveFilterDialog = () => {
    return (
      <Popover
        open={showSaveFilterDialog}
        onClose={() => setShowSaveFilterDialog(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 3, minWidth: 300 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Save Current Filter
          </Typography>
          
          <TextField
            fullWidth
            label="Filter Name"
            value={newFilterName}
            onChange={(e) => setNewFilterName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSaveFilter()}
            size="small"
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowSaveFilterDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveFilter}
              disabled={!newFilterName.trim()}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    );
  };

  // Active filter chips display
  const renderActiveFilters = () => {
    if (!showActiveFilters || (!activeFilters.length && !searchTerm)) return null;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        {searchTerm && (
          <Chip 
            label={`Search: ${searchTerm}`}
            onDelete={handleClearSearch}
            size="small"
            sx={{
              borderRadius: 20,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 2px 5px ${alpha(theme.palette.secondary.main, 0.2)}`,
              },
              '& .MuiChip-deleteIcon': {
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              },
            }}
          />
        )}

        {activeFilters.map((filter, index) => (
          <Chip 
            key={index}
            label={filter.label}
            onDelete={() => filter.onDelete()}
            size="small"
            sx={{
              borderRadius: 20,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 2px 5px ${alpha(theme.palette.secondary.main, 0.2)}`,
              },
              '& .MuiChip-deleteIcon': {
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              },
            }}
          />
        ))}

        {(activeFilters.length > 1 || (activeFilters.length > 0 && searchTerm)) && (
          <Button 
            variant="text" 
            size="small"
            onClick={onClearAllFilters}
            sx={{ 
              color: theme.palette.secondary.main,
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              height: theme.aghrba?.tokens?.buttonHeight?.small || 36,
              '&:hover': {
                color: theme.palette.secondary.light,
                backgroundColor: 'transparent',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Clear All
          </Button>
        )}
      </Box>
    );
  };
  
  return (
    <Box>
      <Box sx={{
        mb: theme.spacing(theme.aghrba.tokens.layout.spacing.element),
        ...theme.aghrba.tokens.layout.flex.between,
        flexWrap: 'wrap',
        gap: 1.5,
        alignItems: 'center'
      }}>
        <Box sx={{ 
          ...theme.aghrba.tokens.layout.flex.row, 
          gap: 1.5, 
          flexGrow: 1, 
          maxWidth: 600,
          alignItems: 'center' 
        }}>
          {/* Search */}
          {!hideSearch && (
            <form onSubmit={handleSearchSubmit} style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <TextField
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ 
                    height: '100%', 
                    marginRight: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    <SearchIcon 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        fontSize: '1.25rem', // Larger icon size
                        ml: 1, // Add margin for better spacing
                        display: 'flex'
                      }} 
                    />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end" sx={{ 
                    height: '100%', 
                    marginLeft: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    <IconButton 
                      onClick={handleClearSearch}
                      sx={{ 
                        color: theme.palette.text.secondary,
                        mr: 0.5,
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.25rem' // Match the search icon size
                        }
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                margin: 0, // Remove any default margin
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.aghrba?.tokens?.borderRadius?.button || 30,
                  backgroundColor: theme.palette.background.light,
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  height: theme.aghrba?.tokens?.buttonHeight?.small || 48.5,
                  display: 'flex',
                  alignItems: 'center', // Center the content vertically
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                    boxShadow: `0 0 0 3px ${theme.palette.secondary.main}40`,
                  },
                  '&:hover': {
                    transform: 'translateY(-1px)',
                  },
                  '& .MuiInputBase-input': {
                    height: 'auto',
                    padding: '0 0 0 8px', // Add padding on the left
                    alignSelf: 'center', // Ensure text is centered
                    fontSize: '0.925rem', // Slightly larger text for better readability
                  },
                  '& .MuiInputAdornment-root': {
                    height: '100%',
                    margin: '0',
                    maxHeight: 'none',
                    alignItems: 'center',
                  }
                }
              }}
            />
            </form>
          )}
          
          {/* Filter button */}
          {(onOpenFilterMenu || builtInMenus) && (
            <Tooltip title="Filter" arrow>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={handleFilterOpen}
                size="small"
                sx={{
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  height: theme.aghrba?.tokens?.buttonHeight?.small || 48.5,
                  borderRadius: theme.aghrba?.tokens?.borderRadius?.button || 30,
                  padding: theme.aghrba?.tokens?.padding?.button?.small || '14px 20px',
                  transition: theme.aghrba?.tokens?.transitions?.standard || 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.main,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Filter
                {hasActiveFilters && (
                  <Badge
                    badgeContent=""
                    color="secondary"
                    variant="dot"
                    sx={{ ml: 1 }}
                  />
                )}
              </Button>
            </Tooltip>
          )}
          
          {/* Sort button */}
          {(onOpenSortMenu || builtInMenus) && (
            <Tooltip title="Sort" arrow>
              <Button
                variant="outlined"
                startIcon={<SortIcon />}
                onClick={handleSortOpen}
                size="small"
                sx={{
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  height: theme.aghrba?.tokens?.buttonHeight?.small || 48.5,
                  borderRadius: theme.aghrba?.tokens?.borderRadius?.button || 30,
                  padding: theme.aghrba?.tokens?.padding?.button?.small || '14px 20px',
                  transition: theme.aghrba?.tokens?.transitions?.standard || 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.main,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Sort
              </Button>
            </Tooltip>
          )}
          
          {/* Saved filters button */}
          {enableSavedFilters && (
            <Tooltip title="Saved Filters" arrow>
              <Button
                variant="outlined"
                startIcon={savedFilters.length > 0 ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                onClick={handleSavedFiltersOpen}
                size="small"
                sx={{
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  height: theme.aghrba?.tokens?.buttonHeight?.small || 48.5,
                  borderRadius: theme.aghrba?.tokens?.borderRadius?.button || 30,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                Saved
              </Button>
            </Tooltip>
          )}

          {/* Advanced filters button */}
          {enableAdvancedFilters && (
            <Tooltip title="Advanced Filters" arrow>
              <Button
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={handleAdvancedFiltersToggle}
                size="small"
                sx={{
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  height: theme.aghrba?.tokens?.buttonHeight?.small || 48.5,
                  borderRadius: theme.aghrba?.tokens?.borderRadius?.button || 30,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                  ...(advancedFiltersOpen && {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  }),
                }}
              >
                Advanced
              </Button>
            </Tooltip>
          )}

          {/* Additional buttons */}
          {additionalButtons}
        </Box>
        
        {/* Add button */}
        {canEdit && onAdd && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            color="secondary"
            onClick={onAdd}
            className="magical" // Use predefined magical class for animation effects
            sx={{
              height: theme.aghrba?.tokens?.buttonHeight?.small || 48.5,
              borderRadius: theme.aghrba?.tokens?.borderRadius?.button || 30,
              padding: theme.aghrba?.tokens?.padding?.button?.small || '14px 20px',
              transition: theme.aghrba?.tokens?.transitions?.standard || 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}
          >
            {addButtonLabel}
          </Button>
        )}
      </Box>

      {/* Quick filters */}
      {quickFilters.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Quick Filters
          </Typography>
          {renderQuickFilters()}
        </Box>
      )}

      {/* Active filter chips */}
      {renderActiveFilters()}

      {/* Advanced filters panel */}
      {renderAdvancedFilters()}

      {/* Built-in menus if enabled */}
      {renderFilterMenu()}
      {renderSortMenu()}
      {renderSavedFiltersMenu()}
      {renderSaveFilterDialog()}
    </Box>
  );
};

export default FilterBar;